import { ActionItemStatus } from '@ekhodealer/ekho-common';

import type { ActionItem, ActionItemCode, GetProgressResponse } from '@ekhodealer/ekho-common';
import type { QueryClient } from '@tanstack/react-query';

import { queries } from '@config/server/queryKeys';

/**
 * Updates the progress cache after a successful action item mutation.
 * If progress is returned from the backend, sets it directly in the cache.
 * If progress is missing (generation failed on the backend), marks the specific
 * action item as COMPLETE in the existing cached data. This avoids cache invalidation
 * since progress is generated asynchronously and a refetch would return stale data.
 */
export function updateProgressCache(
	queryClient: QueryClient,
	servicesProviderId: string,
	orderId: string,
	progress: GetProgressResponse | undefined,
	actionCode?: ActionItemCode,
): void {
	const queryKey =
		queries.orders.detail(servicesProviderId, orderId)._ctx.progress.queryKey;

	if (progress) {
		queryClient.setQueryData(queryKey, progress);
		return;
	}

	if (!actionCode) return;

	queryClient.setQueryData<GetProgressResponse>(queryKey, (old) => {
		if (!old) return undefined;
		const updatedProgress = { ...old.progress };
		for (const key of Object.keys(updatedProgress)) {
			const operation = key as keyof typeof updatedProgress;
			updatedProgress[operation] = updatedProgress[operation].map((item) =>
				item.actionCode === actionCode
					? ({ ...item, status: ActionItemStatus.COMPLETE, completedAt: Date.now() } as ActionItem)
					: item,
			);
		}
		return { ...old, progress: updatedProgress };
	});
}
